import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { client } from 'api/generated/client.gen';
import { handleUnauthorized, injectAuthHeader } from 'interceptors/auth';

type AccessTokenGetter = () => string | undefined;
type RefreshAccessToken = () => Promise<string | null>;

let getAccessToken: AccessTokenGetter = () => undefined;
let refreshAccessToken: RefreshAccessToken = async () => null;
let isInitialized = false;

const isPublicRequest = (requestUrl?: string) => requestUrl?.includes('/public/') ?? false;

export function configureGeneratedApiAuth({
  getAccessToken: nextGetAccessToken,
  refreshAccessToken: nextRefreshAccessToken,
}: {
  getAccessToken: AccessTokenGetter;
  refreshAccessToken: RefreshAccessToken;
}) {
  getAccessToken = nextGetAccessToken;
  refreshAccessToken = nextRefreshAccessToken;
}

export function initializeGeneratedApiClient() {
  if (isInitialized) {
    return;
  }

  client.setConfig({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    withCredentials: true,
  });

  client.instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (isPublicRequest(config.url)) {
      return config;
    }

    return injectAuthHeader(config, getAccessToken());
  });

  client.instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (isPublicRequest(error.config?.url)) {
        return Promise.reject(error);
      }

      return handleUnauthorized(error, refreshAccessToken, (request) => client.instance(request));
    },
  );

  isInitialized = true;
}
